import {useEffect, useId, useRef, useState, type ReactNode} from 'react';
import {Loading, dateTime} from './components';
import {Link} from './router';
import './workspace-ui.css';

export type WorkspacePage = 'workspace' | 'team' | 'tasks' | 'files' | 'settings';
export interface WorkChatRow {
  id: string;
  title: string;
  preview: string | null;
  updatedAt: string | null;
}

function chatTime(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) throw new Error(`Invalid work chat date: ${value}`);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return new Intl.DateTimeFormat(undefined, {hour: 'numeric', minute: '2-digit'}).format(date);
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return new Intl.DateTimeFormat(undefined, {month: 'short', day: 'numeric'}).format(date);
}

export function WorkspaceSidebar({chats, selectedChatId, onSelect, onCreate, busy = false, createDisabled = false,
  loading = false, status, footer, settingsHref, page, onClose, onNavigate}: {
  chats: readonly WorkChatRow[]; selectedChatId: string | null; onSelect: (id: string) => void;
  onCreate: () => Promise<boolean>; busy?: boolean; createDisabled?: boolean; loading?: boolean;
  status?: ReactNode; footer: ReactNode; settingsHref: string;
  page: WorkspacePage;
  onClose: () => void; onNavigate: () => void;
}) {
  const [search, setSearch] = useState('');
  const rows = chats.filter(chat => `${chat.title} ${chat.preview ?? ''}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  return <div className="workspace-sidebar">
    <div className="workspace-sidebar-top"><div className="workspace-sidebar-heading"><h2>Chats</h2>
      <button type="button" className="workspace-sidebar-close" onClick={onClose} aria-label="Close chats">Close</button></div>
      <button type="button" className="workspace-new-chat" disabled={busy || createDisabled}
        onClick={() => void onCreate().then(created => {if (created) onNavigate();})}>New chat</button>
      <label className="workspace-search"><span className="visually-hidden">Search chats</span>
        <input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search chats"/></label>
    </div>
    <div className="workspace-sidebar-chats">{status}{loading && chats.length === 0 && <Loading/>}
      <nav aria-label="Saved work chats"><ul className="workspace-chat-list">{rows.map(row => <li key={row.id}><button type="button"
          className={`workspace-chat-row${selectedChatId === row.id ? ' is-current' : ''}`} aria-current={selectedChatId === row.id ? 'page' : undefined}
          onClick={() => {onSelect(row.id); onNavigate();}} disabled={busy}>
          <span className="workspace-chat-title" title={row.title}>{row.title}</span>
          {row.updatedAt && <time dateTime={row.updatedAt} title={dateTime(row.updatedAt)}>{chatTime(row.updatedAt)}</time>}
        </button></li>)}</ul></nav>
      {!loading && rows.length === 0 && <p className="workspace-sidebar-empty">{search.trim() ? 'No chats match your search.' : 'Your chats will appear here.'}</p>}
    </div>
    <div className="workspace-sidebar-footer"><Link className={`workspace-settings-link${page === 'settings' ? ' is-current' : ''}`} href={settingsHref} onClick={onNavigate}>Settings</Link>{footer}</div>
  </div>;
}

export function WorkspaceShell({sidebar, conversation, children, conversationInMain = false, className = ''}: {
  sidebar: (controls: {onClose: () => void; onNavigate: () => void}) => ReactNode;
  conversation?: ReactNode; children: ReactNode; conversationInMain?: boolean; className?: string;
}) {
  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width:900px)').matches);
  const [sidebarOpen, setSidebarOpen] = useState(() => !window.matchMedia('(max-width:900px)').matches);
  const panel = useRef<HTMLDialogElement>(null), toggle = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  useEffect(() => {
    const query = window.matchMedia('(max-width:900px)');
    const change = (event: MediaQueryListEvent) => {setNarrow(event.matches); setSidebarOpen(!event.matches);};
    query.addEventListener('change', change);
    return () => query.removeEventListener('change', change);
  }, []);
  useEffect(() => {
    const dialog = panel.current;
    if (!dialog) return;
    if (sidebarOpen && !dialog.open) dialog.showModal();
    else if (!sidebarOpen && dialog.open) dialog.close();
  }, [sidebarOpen, narrow]);
  function closeSidebar(): void {
    panel.current?.close();
    setSidebarOpen(false);
    toggle.current?.focus({preventScroll: true});
  }
  const chats = sidebar({onClose: closeSidebar, onNavigate: () => {if (narrow) closeSidebar();}});
  return <div className={`workspace-shell ${sidebarOpen ? 'sidebar-open ' : 'sidebar-closed '}${conversation ? (conversationInMain ? 'has-main-conversation ' : 'has-conversation ') : ''}${className}`}>
    {narrow ? <dialog ref={panel} id={panelId} className="workspace-sidebar-panel" aria-label="Work chats"
      onCancel={event => {event.preventDefault(); closeSidebar();}}
      onClick={event => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeSidebar();
      }}>
      {chats}
    </dialog> : <aside id={panelId} className="workspace-sidebar-panel" aria-label="Work chats" hidden={!sidebarOpen}
      onKeyDown={event => {if (event.key === 'Escape') {event.preventDefault(); closeSidebar();}}}>{chats}</aside>}
    <div className="workspace-main"><div className="workspace-toolbar"><button ref={toggle} type="button"
      className="workspace-sidebar-toggle" aria-expanded={sidebarOpen} aria-controls={panelId}
      aria-label={sidebarOpen ? 'Hide chats' : 'Show chats'}
      onClick={() => sidebarOpen ? closeSidebar() : setSidebarOpen(true)}>
        <span className="workspace-sidebar-toggle-desktop">{sidebarOpen ? 'Hide chats' : 'Show chats'}</span>
        <span className="workspace-sidebar-toggle-mobile" aria-hidden="true">Chats</span>
      </button></div>
      <div className="workspace-body">{children}</div>{conversationInMain && conversation}</div>
    {!conversationInMain && conversation}</div>;
}
